import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {CipaFacilConfirmationService} from '../../../@cipafacil/services/confirmation';
import {MatDialog} from '@angular/material/dialog';
import {environment} from '../../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    private _isRefreshing = false;
    private _refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _router: Router,
        private _cipaFacilConfirmationDialog: CipaFacilConfirmationService,
        private _matDialog: MatDialog
    ) {
    }

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        // Clone the request object
        let newReq = req.clone();

        // Skip default auth headers for OpenAI and Easy Access (uses own JWT)
        if (req.url.includes('https://api.openai.com') || req.url.includes('easy-access')) {
            newReq = req;
        } else {
            if (req.reportProgress && this._authService.nodeToken) {
                newReq = req.clone({
                    headers: req.headers.set('authorization', 'Bearer ' + this._authService.nodeToken).set('refreshtoken', this._authService.refreshToken),
                    reportProgress: true
                });
            } else {
                if ( this._authService.accessToken && this._authService.refreshToken) {
                    if (req.url === 'https://api.cipa.com.br/smartged/getAcessoTemporarioS3') {
                        newReq = req.clone({
                            headers: req.headers.set('authorization', this._authService.accessToken)
                                .set('content-type', 'application/json')
                        });
                    } else {
                        newReq = req.clone({
                            headers: req.headers.set('authorization', this._authService.accessToken)
                                .set('refreshtoken', this._authService.refreshToken)
                                .set('content-type', 'application/json')
                        });
                    }
                }
            }
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {

                // Catch "401 Unauthorized" responses
                if ( error instanceof HttpErrorResponse && error.status === 401 )
                {
                    // Only attempt refresh for Node backend requests (not refresh-token itself)
                    if (req.url.startsWith(environment.node) && !req.url.includes('/refresh-token') && this._authService.nodeRefreshToken) {
                        return this._handle401Error(req, next);
                    }

                    // For non-node requests or if no refresh token, show dialog
                    this._handleAuthFailure();
                }

                return throwError(error);
            })
        );
    }

    private _handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        if (!this._isRefreshing) {
            this._isRefreshing = true;
            this._refreshTokenSubject.next(null);

            return this._authService.refreshAccessToken().pipe(
                switchMap((response: any) => {
                    this._isRefreshing = false;
                    this._refreshTokenSubject.next(response.access_token);

                    // Retry the original request with the new token
                    const newReq = req.clone({
                        headers: req.headers.set('authorization', 'Bearer ' + this._authService.nodeToken)
                    });
                    return next.handle(newReq);
                }),
                catchError((err) => {
                    this._isRefreshing = false;
                    this._signOutAndRedirect();
                    return throwError(err);
                })
            );
        } else {
            // Wait for the refresh to complete, then retry
            return this._refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(() => {
                    const newReq = req.clone({
                        headers: req.headers.set('authorization', 'Bearer ' + this._authService.nodeToken)
                    });
                    return next.handle(newReq);
                })
            );
        }
    }

    private _handleAuthFailure(): void
    {
        if (this._matDialog.getDialogById('dialog-unauthorized')) {
            return;
        }

        this._cipaFacilConfirmationDialog.open({
            id: 'dialog-unauthorized',
            title: 'Desculpe :(',
            message: 'Voce nao possui permissao para acessar essa funcionalidade!',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Ir para dashboard',
                    color: 'warn'
                },
                cancel: {
                    show: false,
                    label: 'Ir para dashboard'
                }
            },
            dismissible: false
        })
            .afterClosed()
            .subscribe(() => {
                return this._router.navigateByUrl('/dashboard');
            });
    }

    private _signOutAndRedirect(): void
    {
        this._authService.signOut().subscribe(() => {
            this._router.navigateByUrl('/sign-in');
        });
    }
}
